import { ChangeDetectorRef, signal, WritableSignal } from '@angular/core';
import { catchError, of, take } from 'rxjs';
import { DashboardService } from '../../../../data-access/src/lib/dashboard/dashboard.service';
import {
  vaultResolveAdminUsersActor,
  vaultResolveIsSuperadmin,
  vaultUserIsSuperadminFromSession,
  type VaultAdminUsersActor,
} from '@nebula/shared-vault/data-access';

export type VaultAdminModuleAccessMode = 'superadmin_only' | 'admin_users';

/**
 * Mismo patrón que `PreservationMessagesComponent`: JWT/sesión primero,
 * reintentos por hidratación y fallback con `GET /api/dashboard/stats`.
 */
export function bootstrapVaultAdminModuleAccess(
  canManage: WritableSignal<boolean>,
  mode: VaultAdminModuleAccessMode,
  dashboard: DashboardService,
  cdr: ChangeDetectorRef,
  onGranted?: () => void,
  actor?: WritableSignal<VaultAdminUsersActor>,
): void {
  let initialLoadDone = false;
  const runInitialLoad = () => {
    if (initialLoadDone) return;
    initialLoadDone = true;
    onGranted?.();
  };

  const isGranted = (dashboardRole?: string | null): boolean => {
    if (mode === 'superadmin_only') {
      return vaultResolveIsSuperadmin(dashboardRole);
    }
    return vaultResolveAdminUsersActor(dashboardRole) !== 'denied';
  };

  const grant = (dashboardRole?: string | null) => {
    if (!isGranted(dashboardRole)) return;
    if (actor) {
      actor.set(vaultResolveAdminUsersActor(dashboardRole));
    }
    if (!canManage()) {
      canManage.set(true);
    }
    runInitialLoad();
    cdr.markForCheck();
  };

  grant();
  queueMicrotask(() => grant());
  setTimeout(() => grant(), 200);

  dashboard
    .getStats()
    .pipe(take(1), catchError(() => of(null)))
    .subscribe((stats) => {
      grant(stats?.role);
    });
}

export function createAdminModuleAccessSignal(mode: VaultAdminModuleAccessMode): WritableSignal<boolean> {
  if (mode === 'superadmin_only') {
    return signal(vaultUserIsSuperadminFromSession());
  }
  return signal(vaultResolveAdminUsersActor() !== 'denied');
}
